import * as React from 'react'
import { CheckCircle, XCircle } from 'react-feather'
import { LuCloudUpload } from 'react-icons/lu'

import { Spinner } from '@/components/ui/spinner'
import { FILE_UPLOAD_BOX_STATE } from '@/lib/constants'
import { cn } from '@/lib/utils'
import { FileUploadBoxState } from '@/types/misc'

interface Props {
  onChange: (file: File) => void
  accept?: string
  state: FileUploadBoxState
  message?: string
  minHeight?: string
  inputRef?: React.RefObject<HTMLInputElement | null>
}

export const FileUploadBox: React.FC<Props> = ({
  onChange,
  state = FILE_UPLOAD_BOX_STATE.disabled,
  accept = '*/*',
  message,
  inputRef,
}: Props) => {
  const isEnabled = state === FILE_UPLOAD_BOX_STATE.enabled

  function renderIcon() {
    if (
      state === FILE_UPLOAD_BOX_STATE.disabled ||
      state === FILE_UPLOAD_BOX_STATE.enabled
    ) {
      return <LuCloudUpload className="text-3xl text-primary" />
    }

    if (state === FILE_UPLOAD_BOX_STATE.uploading) {
      return <Spinner className="size-8" />
    }

    if (state === FILE_UPLOAD_BOX_STATE.success) {
      return <CheckCircle className="text-success" size={48} />
    }

    if (state === FILE_UPLOAD_BOX_STATE.error) {
      return <XCircle className="text-destructive" size={48} />
    }

    return null
  }

  return (
    <div
      className={cn(
        `relative flex flex-col items-center justify-center rounded-xl border-2
        border-border bg-white hover-elevate cursor-pointer transition-all py-7
        px-4`,
        {
          'hover:bg-gray-100/60 border-dashed':
            state === FILE_UPLOAD_BOX_STATE.enabled,
        },
        { 'border-success': state === 'success' },
        { 'border-destructive': state === 'error' },
      )}
    >
      {renderIcon()}

      {message && (
        <p className="mt-3 mb-0 text-[13px] text-muted-foreground">{message}</p>
      )}

      <input
        type="file"
        accept={accept}
        disabled={!isEnabled}
        className={
          'absolute top-0 left-0 block h-full w-full opacity-0 ' +
          (isEnabled ? 'cursor-pointer' : 'pointer-events-none')
        }
        ref={inputRef}
        onChange={(e) => {
          if (e.target.files === null) {
            return
          }

          const file = e.target.files[0]

          if (file) {
            onChange(file)
          }
        }}
      />
    </div>
  )
}
